import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Play, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { getEmbedUrl, isDirectVideo } from "@/lib/embed";
import VoteButtons from "@/components/VoteButtons";
import ShareButtons from "@/components/ShareButtons";
import CommentSection from "@/components/CommentSection";
import { renderWithLinks, tagLink } from "@/lib/autolink";

export default function BlogDetail() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/blog/${id}`)
      .then(({ data }) => setPost(data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#060810] flex flex-col items-center justify-center gap-4">
        <p className="text-slate-400">Post not found.</p>
        <Link to="/blog" className="text-cyan-400">Back to Journal</Link>
      </div>
    );
  }

  if (!post) {
    return <div className="min-h-screen bg-[#060810] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>;
  }

  const embed = post.type === "video" ? getEmbedUrl(post.video_url) : null;

  return (
    <div className="min-h-screen bg-[#060810]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-400 mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to Journal
        </Link>

        <span className="text-[10px] font-mono-caption uppercase tracking-widest px-3 py-1 rounded-full glass border border-cyan-300/30 text-cyan-300 inline-flex items-center gap-1 mb-4">
          {post.type === "video" ? <><Play className="w-3 h-3" /> Vlog</> : <><FileText className="w-3 h-3" /> Article</>}
        </span>

        <h1 data-testid="blog-title" className="font-heading font-extrabold text-white text-4xl sm:text-5xl leading-tight mb-4">
          {post.title}
        </h1>
        <div className="flex items-center gap-3 text-sm text-slate-400 mb-8">
          <span className="text-slate-300 font-semibold">{post.author_name}</span>
          {post.tags?.map((t) => (
            <Link key={t} to={tagLink(t)} className="px-2 py-0.5 rounded-full glass text-xs text-cyan-300 hover:text-white transition-colors">#{t}</Link>
          ))}
        </div>

        {post.type === "video" && embed && (
          <div className="rounded-2xl overflow-hidden glass mb-8 aspect-video" data-testid="blog-video">
            {isDirectVideo(embed) ? (
              <video src={embed} controls poster={post.cover_image} className="w-full h-full object-cover" />
            ) : (
              <iframe title={post.title} src={embed} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" />
            )}
          </div>
        )}

        {post.type === "article" && post.cover_image && (
          <img src={post.cover_image} alt={post.title} className="w-full rounded-2xl mb-8 aspect-video object-cover" />
        )}

        <div data-testid="blog-body" className="text-slate-300 text-lg leading-relaxed whitespace-pre-wrap mb-10">
          {renderWithLinks(post.body)}
        </div>

        <div className="flex items-center justify-between py-4 border-y border-[#232A4D]">
          <VoteButtons type="blog" item={post} orientation="horizontal" />
          <ShareButtons title={post.title} path={`/blog/${post.id}`} />
        </div>

        <CommentSection parentType="blog" parentId={post.id} />
      </div>
    </div>
  );
}
